import { ChangeEvent, DragEvent, useMemo } from 'react';
import { FileUploadInvalidPayload, UseFileUploadProps } from './types';

const DEFAULT_ACCEPTED_FORMATS = ['csv', 'xls', 'xlsx'];

const normalizeFormat = (format: string) => format.trim().replace(/^\./, '').toLowerCase();

const getFileExtension = (fileName: string) => {
  const extension = fileName.split('.').pop()?.trim().toLowerCase();

  if (!extension || extension === fileName.toLowerCase()) {
    return null;
  }

  return extension;
};

const formatFileSize = (size: number) => {
  if (size < 1024) {
    return `${size}B`;
  }

  if (size < 1024 * 1024) {
    return `${Math.max(1, Math.round(size / 1024))}KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(1).replace('.0', '')}MB`;
};

export const useFileUpload = ({
  value = null,
  onChange,
  onInvalidFile,
  acceptedFormats = DEFAULT_ACCEPTED_FORMATS,
}: UseFileUploadProps) => {
  const normalizedAcceptedFormats = useMemo(
    () => acceptedFormats.map(normalizeFormat).filter(Boolean),
    [acceptedFormats],
  );

  const currentFileExtension = value ? getFileExtension(value.name) : null;
  const formattedFileSize = value ? formatFileSize(value.size) : null;

  const notifyInvalidFile = (file: File, detectedExtension: string | null) => {
    const payload: FileUploadInvalidPayload = {
      file,
      reason: 'invalid_extension',
      acceptedFormats: normalizedAcceptedFormats,
      detectedExtension,
    };

    onInvalidFile?.(payload);
  };

  const validateFile = (file: File) => {
    const detectedExtension = getFileExtension(file.name);

    if (!detectedExtension || !normalizedAcceptedFormats.includes(detectedExtension)) {
      notifyInvalidFile(file, detectedExtension);
      return false;
    }

    return true;
  };

  const selectFile = (file: File | null) => {
    if (!file) {
      onChange?.(null);
      return;
    }

    if (!validateFile(file)) {
      return;
    }

    onChange?.(file);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    selectFile(event.target.files?.[0] ?? null);
    event.target.value = '';
  };

  const handleDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    selectFile(event.dataTransfer.files?.[0] ?? null);
  };

  const handleDragOver = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
  };

  const clearFile = () => {
    onChange?.(null);
  };

  return {
    file: value,
    fileExtension: currentFileExtension,
    formattedFileSize,
    acceptedFormats: normalizedAcceptedFormats,
    handleInputChange,
    handleDrop,
    handleDragOver,
    clearFile,
  };
};
